import { DateTimePicker, TimeZoneCombobox } from '@/components'
import type { EmailSendRuleDto } from '@/types/touchPoint'

interface ScheduledSendTimeFieldsProps {
  /** The Scheduled rule being edited. */
  value: EmailSendRuleDto
  onChange: (rule: EmailSendRuleDto) => void
  /** Error message for the local-time field (from the form's rule validator). */
  error?: string
}

/**
 * Tz-aware send-time editor for a Scheduled email rule (ORB-352).
 *
 * Binds a local wall-clock `datetime-local` picker to `ScheduledSendLocalTime` and an
 * IANA timezone selector to `ScheduledSendTimeZone`. The server derives the absolute
 * `SendAtAbsolute` instant from this pair, so we never set it here.
 *
 * RecipientLocal mode (per-recipient local delivery) is a gated fast-follow — not surfaced here.
 */
export function ScheduledSendTimeFields({ value, onChange, error }: ScheduledSendTimeFieldsProps) {
  // `datetime-local` wants "YYYY-MM-DDTHH:mm"; the wire value carries seconds.
  const localInputValue = (value.ScheduledSendLocalTime ?? '').slice(0, 16)

  // The form surfaces one rule-level error, but it can be about either field. Route it to the field
  // actually at fault using this component's own values — mirroring the validator's precedence
  // (local time is checked before timezone) rather than string-matching the message text.
  const localTimeMissing = !localInputValue || localInputValue.startsWith('T')
  const localTimeError = error && localTimeMissing ? error : undefined
  const timeZoneError = error && !localTimeMissing && !value.ScheduledSendTimeZone ? error : undefined

  const handleLocalChange = (event: Event) => {
    const raw = (event.target as HTMLInputElement).value // "" or "YYYY-MM-DDTHH:mm"
    onChange({ ...value, ScheduledSendLocalTime: raw ? `${raw}:00` : null })
  }

  return (
    <div className="space-y-3">
      <DateTimePicker
        label="Send date & time"
        required
        labelSize="xs"
        value={localInputValue}
        onChange={handleLocalChange}
        error={localTimeError}
        helperText={localTimeError ? undefined : 'Local wall-clock time in the selected timezone.'}
      />
      <TimeZoneCombobox
        label="Timezone"
        required
        name="ScheduledSendTimeZone"
        labelSize="xs"
        value={value.ScheduledSendTimeZone ?? ''}
        onChange={(tz) => onChange({ ...value, ScheduledSendTimeZone: tz || null })}
        error={timeZoneError}
      />
    </div>
  )
}
